import type { Pinia } from 'pinia'
import { watch } from 'vue'
import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useOnboardingStore } from '@/stores/onboarding'
import { onboardingLog } from '@/services/onboardingLog'

const productRoutes = new Set(['home', 'studio', 'create', 'profile', 'plans'])
const authBlockedRoutes = new Set(['onboarding', 'plans'])

function isCheckoutReturn(to: RouteLocationNormalized): boolean {
  return to.query.checkout === 'success' || to.query.checkout === 'cancelled'
}

function onboardingRedirect(to: RouteLocationNormalized, pinia: Pinia): RouteLocationRaw | undefined {
  const auth = useAuthStore(pinia)
  const onboarding = useOnboardingStore(pinia)
  if (to.name === 'onboarding') {
    if (!auth.isAuthenticated) return { name: 'landing', query: { auth: 'signup' }, replace: true }
    if (onboarding.completed) return { name: 'studio', replace: true }
    return
  }
  // A paid bonus can return in a new browser. Verify payment before applying local onboarding redirects.
  if (to.name === 'studio' && ['success', 'cancelled'].includes(String(to.query.welcome_bonus))) return
  if (!productRoutes.has(String(to.name)) || !auth.isAuthenticated || onboarding.completed) return
  if (to.name === 'plans') {
    // Stripe возвращает на канонический /plans; успешность подтверждает сам PlansView через API.
    if (isCheckoutReturn(to)) return
    if (to.query.onboarding === '1' && onboarding.phase === 'plans') return
  }
  onboardingLog('debug', 'first_entry_redirect', { route: String(to.name) })
  return { name: 'onboarding', replace: true }
}

// Регистрация до app.use(router) перехватывает и первую загрузку deep link.
export function installOnboardingGuard(router: Router, pinia: Pinia) {
  const auth = useAuthStore(pinia)
  const onboarding = useOnboardingStore(pinia)
  watch(() => [auth.identity?.user_id, onboarding.completed], () => {
    const target = onboardingRedirect(router.currentRoute.value, pinia)
    if (target) void router.replace(target)
  })
  return router.beforeEach(async (to) => {
    // The return page owns bounded restoration and shows its loader immediately.
    // Waiting here hides that page and bypasses its confirmation deadline.
    if (authBlockedRoutes.has(String(to.name)) && !(to.name === 'plans' && to.query.checkout === 'success')) {
      await auth.initialize()
    }
    // Фиксируем layout на время маршрута: complete() не должен перемонтировать paywall
    // до router.replace и повторно запустить подтверждение оплаты.
    to.meta.onboardingPlans = to.name === 'plans' && !onboarding.completed
      && (isCheckoutReturn(to) || (to.query.onboarding === '1' && onboarding.phase === 'plans'))
    return onboardingRedirect(to, pinia)
  })
}
